import { z } from 'zod'
import { userRefSchema } from './primitives.js'

/** Módulo Entrevistas — visible a los tres roles. No tiene campos de $$. */

export const interviewBlockSchema = z.enum(['A', 'B', 'C', 'D', 'E'])
export type InterviewBlock = z.infer<typeof interviewBlockSchema>

/** Cada bloque tiene exactamente 3 preguntas, numeradas 1 a 3. */
export const PREGUNTAS_POR_BLOQUE = 3

export const interviewQuestionNumberSchema = z
  .number()
  .int()
  .min(1)
  .max(PREGUNTAS_POR_BLOQUE)

export interface InterviewBlockDef {
  bloque: InterviewBlock
  titulo: string
  /** Por qué se pregunta esto: se muestra arriba del bloque en el formulario. */
  intencion: string
  preguntas: readonly [string, string, string]
}

/**
 * Cuestionario de entrevista, cinco bloques de tres preguntas.
 * Es contenido versionado en el contrato, no configuración: la vista de
 * insights agrupa por `bloque` + `preguntaNro`, así que renumerar rompe la
 * comparabilidad histórica. Para cambiar el cuestionario, agregar preguntas
 * nuevas antes que reescribir las existentes.
 */
export const INTERVIEW_BLOCKS: readonly InterviewBlockDef[] = [
  {
    bloque: 'A',
    titulo: 'Adquisición',
    intencion: 'Cómo nos encontró y qué lo decidió a probar.',
    preguntas: [
      '¿Cómo llegaste a Laburen y qué estabas buscando resolver en ese momento?',
      '¿Qué otras alternativas evaluaste antes de decidirte?',
      '¿Qué fue lo que finalmente te decidió a firmar?',
    ],
  },
  {
    bloque: 'B',
    titulo: 'Activación',
    intencion: 'Qué tan rápido llegó al primer valor y qué lo frenó.',
    preguntas: [
      '¿Cuánto tardaste desde firmar hasta ver el primer resultado concreto?',
      '¿Qué fue lo más difícil de poner en marcha al empleado digital?',
      '¿Qué te hubiera hecho arrancar más rápido?',
    ],
  },
  {
    bloque: 'C',
    titulo: 'Valor y retención',
    intencion: 'Por qué sigue pagando y qué lo haría irse.',
    preguntas: [
      '¿Qué es lo que hoy no podrías dejar de usar?',
      'Si tuvieras que dar de baja mañana, ¿cuál sería el motivo?',
      '¿Qué resultado medible atribuís a Laburen en los últimos tres meses?',
    ],
  },
  {
    bloque: 'D',
    titulo: 'Expansión y referidos',
    intencion: 'Dónde hay más superficie con la misma cuenta.',
    preguntas: [
      '¿Qué otro proceso de tu empresa le darías a un empleado digital?',
      '¿Qué necesitarías para sumar más empleados digitales este trimestre?',
      '¿A quién le recomendarías Laburen y por qué a esa persona?',
    ],
  },
  {
    bloque: 'E',
    titulo: 'Señales de producto',
    intencion: 'Qué construir después, dicho por quien lo usa.',
    preguntas: [
      '¿Qué es lo que más te frustra del producto hoy?',
      '¿Qué le pedirías al equipo que haga en los próximos 90 días?',
      '¿Qué parte de tu trabajo sigue siendo manual y no debería?',
    ],
  },
]

/** Texto de una pregunta. null si el bloque o el número no existen. */
export function interviewQuestion(
  bloque: InterviewBlock,
  preguntaNro: number,
): string | null {
  const def = INTERVIEW_BLOCKS.find((b) => b.bloque === bloque)
  if (def === undefined) return null
  if (!Number.isInteger(preguntaNro)) return null
  if (preguntaNro < 1 || preguntaNro > PREGUNTAS_POR_BLOQUE) return null
  return def.preguntas[preguntaNro - 1] as string
}

/** Total de preguntas del cuestionario: 5 bloques × 3. */
export function totalPreguntas(): number {
  return INTERVIEW_BLOCKS.length * PREGUNTAS_POR_BLOQUE
}

export const interviewStateSchema = z.enum(['pendiente', 'agendada', 'hecha'])
export type InterviewState = z.infer<typeof interviewStateSchema>

export const INTERVIEW_STATE_LABELS: Record<InterviewState, string> = {
  pendiente: 'Pendiente',
  agendada: 'Agendada',
  hecha: 'Hecha',
}

export const interviewAnswerSchema = z.object({
  bloque: interviewBlockSchema,
  preguntaNro: interviewQuestionNumberSchema,
  /** Copia del enunciado al momento de responder: el histórico no se reescribe. */
  pregunta: z.string(),
  /** Vacío = no respondida. La UI la muestra como pendiente, no como "sin dato". */
  respuesta: z.string(),
})
export type InterviewAnswer = z.infer<typeof interviewAnswerSchema>

export const interviewSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  clientNombre: z.string(),
  estado: interviewStateSchema,
  /** ISO 8601. null si todavía no se agendó. */
  fecha: z.string().nullable(),
  entrevistador: userRefSchema.nullable(),
  respuestas: z.array(interviewAnswerSchema),
  /** Cuántas de las 15 preguntas tienen respuesta. Derivado. */
  respondidas: z.number().int().nonnegative(),
})
export type Interview = z.infer<typeof interviewSchema>

/** Cuenta respuestas no vacías. Derivado en lectura, no se almacena. */
export function contarRespondidas(
  respuestas: readonly InterviewAnswer[],
): number {
  return respuestas.filter((r) => r.respuesta.trim().length > 0).length
}

export const saveInterviewAnswersSchema = z.object({
  interviewId: z.string(),
  respuestas: z
    .array(
      z.object({
        bloque: interviewBlockSchema,
        preguntaNro: interviewQuestionNumberSchema,
        respuesta: z.string().max(4000),
      }),
    )
    .min(1),
})
export type SaveInterviewAnswersInput = z.infer<
  typeof saveInterviewAnswersSchema
>

/** Vista de insights: respuestas de todas las entrevistas, por pregunta. */
export const interviewInsightSchema = z.object({
  bloque: interviewBlockSchema,
  bloqueTitulo: z.string(),
  preguntaNro: interviewQuestionNumberSchema,
  pregunta: z.string(),
  respuestas: z.array(
    z.object({
      interviewId: z.string(),
      clientNombre: z.string(),
      respuesta: z.string(),
      fecha: z.string().nullable(),
    }),
  ),
})
export type InterviewInsight = z.infer<typeof interviewInsightSchema>

export const interviewsPayloadSchema = z.object({
  entrevistas: z.array(interviewSchema),
  insights: z.array(interviewInsightSchema),
})
export type InterviewsPayload = z.infer<typeof interviewsPayloadSchema>
